export type Provider = "yumemi" | "vuilabs";
export type TestSuiteKey = "yumemi_tts" | "vuilabs_tts" | "reference_boundary";

export interface TestSuite {
  key: TestSuiteKey;
  label: string;
  enabled: boolean;
  speed_min: number;
  speed_max: number;
  speed_step: number;
}

export interface Voice {
  model_id?: "luna-tts" | "luna-tts-1.5" | "luna-tts-character";
  source?: string;
  voice_id: string;
  display_name: string;
  language: string | null;
  status: string | null;
}

export interface VoiceCatalogResponse {
  luna_environment?: "production" | "test";
  luna_test_available?: boolean;
  provider: Provider;
  voices: Voice[];
  catalog_version: string;
  fetched_at: string;
  age_seconds: number;
  cache_ttl_seconds: number;
  fresh_for_seconds: number;
  cache_source: "provider" | "redis" | "memory";
  stale: boolean;
  refreshing: boolean;
  warning: string | null;
}

export interface Summary {
  statuses?: Record<string,number>;
  total: number;
  success: number;
  failed: number;
  remaining: number;
  progress: number;
  latency?: { p50: number | null };
}

export interface DatasetSummary {
  enabled_cases: number;
  scenes: Record<string, number>;
  expected_styles: Record<string, number>;
  languages?: Record<string, number>;
}

export interface Dataset {
  lifecycle_actions?: string[];
  has_references?: boolean;
  last_used_at?: string;
  version_number?: number;
  id: string;
  dataset_id?: string;
  original_name: string;
  source_filename?: string;
  summary: DatasetSummary;
  created_at?: string;
  content_sha256?: string;
  manifest_sha256?: string;
  sealed_at?: string;
  version_label?: string;
  status?: string;
}

export interface Run {
  id: string;
  status: string;
  run_type?: TestSuiteKey | "standard_tts" | string;
  config: Record<string, unknown>;
  summary: Summary;
  created_at?: string;
  updated_at?: string;
  version?: number;
  planned_requests?: number;
  plan_sha256?: string | null;
  dataset_version_id?: string;
  created_by?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
  voice_catalog_snapshot?: Record<string, unknown>;
}

export interface RunDetail {
  progress?: RunProgressSnapshot;
  run: Run;
  summary: Summary;
  log: string[];
  alive: boolean;
}

export interface Review {
  status: string;
  issue_category?: string;
  issue_label?: string;
  issue_time?: string;
  severity?: string;
  comment?: string;
  root_cause?: string;
  reviewer?: string;
}

export interface Candidate {
  task_id: string;
  text_id: string;
  text: string;
  voice_id: string;
  expected_style: string;
  actual_style: string;
  duration: number;
  audio_url: string;
  finding: {
    category: string;
    labels: string[];
    times: number[];
    reason_types?: Array<"signal_degradation" | "content_accuracy" | "prosody" | "contract" | string>;
    reasons?: Array<{
      type: "signal_degradation" | "content_accuracy" | "prosody" | "contract" | string;
      labels: string[];
      metric?: string | null;
      error_rate?: number | null;
      evidence?: {
        threshold?: number | null;
        policy_version?: string;
        asr_provider?: string;
        asr_model?: string;
        language?: string | null;
        boundaries?: Array<Record<string, unknown>>;
      };
    }>;
  };
  review?: Review;
}

export interface PlatformReviewFinding {
  id: string;
  label: string;
  category: string;
  severity: string;
  confidence: number | null;
  auto_flag?: boolean | null;
  detector_version: string;
  policy_version: string;
  evidence: Record<string, unknown>;
}

export interface JapaneseReview {
 version:string;state:'text_match'|'orthographic_variant'|'recognition_ambiguous'|'content_difference'|'unavailable';
 queue:'none'|'context'|'focus';reason:string;reference_reading?:string|null;transcript_reading?:string|null;
}
export interface ContentReview {
 cer_review?: {language:string;raw_error_rate:number|null;review_error_rate:number|null;threshold:number|null;operator:string;threshold_status:string;review_hints:string[]};
 japanese_review?:JapaneseReview;
 status: string;
 word_diff: { unit?: "character"|"word"; operations: Array<{operation:string;expected:string;recognized:string;start?:number;end?:number;localization?:string}> };
 regions: Array<{start:number;end:number;expected:string;primary:string;assessment:string;secondary:{status:string;text?:string;model?:string;error_code?:string}}>;
}
export interface ReviewTranscript {
 content_review?: ContentReview | null;
 status: "succeeded" | "failed" | "unavailable"; text: string | null; reason?: string | null;
 evaluation_id?: string; attempt_id?: string; audio_sha256?: string; model?: string; model_revision?: string; error_code?: string | null;
}

export interface PlatformReviewCandidate {
  id: string;
  run_id: string;
  run_task_record_id: string;
  asr_result?: ReviewTranscript;
  task_id: string;
  status: string;
  priority: number;
  case_context?: CaseContext;
  task_definition: Record<string, unknown>;
  audio_attempt_id: string | null;
  audio_artifact_id: string | null;
  created_at: string;
  finding: PlatformReviewFinding | null;
  audio_review_snapshot?: string | null;
  reassessment?: { id: string; rule_version: string; created_at: string; review_eligible: boolean } | null;
  original_finding?: PlatformReviewFinding | null;
  related_findings?: Array<{ candidate_id: string; review_status: string; finding: PlatformReviewFinding | null;
    can_review?: boolean; latest_decision?: { status: string; comment: string; severity: string } | null;
    reassessment?: { id: string; rule_version: string; created_at: string; review_eligible: boolean } | null;
    original_finding?: PlatformReviewFinding | null }>;
  latest_decision: null | {
    id: string;
    status: string;
    issue_category: string;
    issue_label: string;
    issue_time: string;
    severity: string;
    comment: string;
    root_cause: string;
    reviewer_id: string | null;
  };
}

export interface PlatformReviewClaim {
  candidate: PlatformReviewCandidate;
  lease: {
    candidate_id: string;
    reviewer_id: string;
    lease_token: string;
    leased_at: string;
    lease_until: string;
    version: number;
  };
}

export interface PlatformReviewQueueStats {
  run_id: string | null;
  total: number;
  audio_count?: number;
  unavailable_audio_count?: number;
  count_unit?: "finding";
  available: number;
  active_leases: number;
  expired_leases: number;
  statuses: Record<string, number>;
  reassessment?: { assessed_count: number; observation_count: number; latest_at: string | null };
}

export interface BadCase {
  run_id: string;
  provider: string;
  run_type: string;
  task_id: string;
  text_id: string;
  text: string;
  voice_id: string;
  run_index: number;
  audio_url: string;
  audio_file: string;
  duration: number;
  expected_style: string;
  actual_style: string;
  request_id: string;
  trace_id: string;
  reference_id: string;
  model_profile: string;
  capability_version: string;
  checkpoint: string;
  issue_category: string;
  issue_label: string;
  issue_time: string;
  severity: string;
  conclusion: string;
  root_cause: string;
  reviewer: string;
  reviewed_at: string;
}

export interface GroupedResponse<T> {
  items: T[];
  groups: Record<string, number>;
  platforms?: Record<string, number>;
  run_types?: Record<string, number>;
}

export interface DatasetCaseSnapshot {
  id: string;
  dataset_version_id: string;
  benchmark_case_id: string;
  ordinal: number;
  enabled: boolean;
  scene: string;
  language: string;
  input_text: string;
  expected_spoken_text: string;
  expected_style: string;
  split: string;
  content_sha256: string;
  definition: Record<string, unknown>;
}

export interface StatusCounts {
  total: number;
  statuses: Record<string, number>;
}

export interface RunProgressSnapshot {
  run_id: string;
  status: string;
  version: number;
  planned_requests: number;
  tasks: StatusCounts;
  terminal_attempts: StatusCounts;
  quality_completed: number;
  quality_statuses?: Record<string,number>;
  asr_latest_statuses?: Record<string,number>;
  asr_completed: number;
  asr_failed: number;
  candidates: StatusCounts;
  last_event_id: number;
  generated_at: string;
}

export interface RunTaskRecord {
  record_id: string;
  run_id: string;
  task_id: string;
  ordinal: number;
  request_id: string;
  status: string;
  definition: Record<string, unknown>;
  created_at: string;
}

export interface PlatformBadCase {
  id: string;
  candidate_id: string;
  status: string;
  severity: string;
  owner_id: string | null;
  version: number;
  review_status: string;
  run_id: string;
  task_id: string;
  dataset_version_id: string;
  created_at: string;
  updated_at: string;
  case_context?: CaseContext;
}

export interface CaseContext {
  provider?: string | null;
  model_version?: string | null;
  model_version_kind?: "checkpoint" | "model_version" | "deployment" | null;
  voice_id?: string | null;
  reference_id?: string | null;
  discovered_at?: string | null;
  confirmed_at?: string | null;
}

export interface CaseFilters {
  issue_kind?: "subjective" | "objective" | "unknown";
  issue_tag?: string;
  issue_label?: string;
  issue_category?: string;
  run_id?: string;
  provider?: string;
  model_version?: string;
  voice_id?: string;
  reference_id?: string;
  date_from?: string;
  date_to?: string;
  q?: string;
  severity?: string;
  date_field?: "discovered" | "confirmed";
}

export interface BadCaseAudioGroup {
  audio_key: string;
  attempt_id: string | null;
  audio_artifact_id: string | null;
  run_id: string;
  task_id: string;
  case_context?: CaseContext;
  bad_case_ids: string[];
  reasons: Array<{ bad_case_id: string; label: string; category: string; source?:'human'|'machine'; categories?:string[]; recorded_label?: string | null;
    comment: string; issue_time: string; severity: string; status: string; matches_filter: boolean; confirmed_at: string }>;
}
export interface BadCaseAudioPage {
  total: number; offset: number; limit: number; count_unit: "audio";
  issue_count: number; matched_issue_count: number; severe_audio_count: number;
  category_facets: Array<{ value: string; audio_count: number }>;
  label_facets: Array<{ value: string; audio_count: number; name?:string; kind?:string; category?:string;source?:'human'|'machine'|'mixed' }>;
  items: BadCaseAudioGroup[];
}

export interface ReviewFindingSummary {
  candidate_id: string;
  run_id: string;
  task_id: string;
  review_status: string;
  priority: number;
  label: string | null;
  category: string | null;
  created_at: string;
  case_context?: CaseContext;
}

export interface ReviewAudioFilters extends CaseFilters {
  label?: string;
  single_reason?: boolean;
  screening?: "current" | "observation" | "all" | "focus" | "context";
}

export interface ReviewAudioGroup {
  audio_key: string;
  attempt_id: string | null;
  candidate_id: string;
  run_id: string;
  task_id: string;
  created_at: string;
  case_context?: CaseContext;
  finding_count: number;
  reason_count: number;
  statuses: Record<string, number>;
  historical_finding_count?: number;
  findings: Array<{ candidate_id: string; label: string | null; review_status: string; priority: number;
    matches_screening?: boolean; machine_review_eligible?: boolean; reassessed_at?: string | null }>;
}

export interface PlatformBadCaseDetail extends PlatformBadCase {
  run_task_id: string;
  task_definition: Record<string, unknown>;
  audio_attempt_id: string | null;
  audio_artifact_id: string | null;
  latest_decision: null | {
    id: string;
    decision_version: number;
    status: string;
    issue_category: string;
    issue_label: string;
    issue_time: string;
    severity: string;
    reviewer_id: string | null;
    created_at: string;
  };
}

export interface RegressionSet {
  can_write?: boolean;
  id: string;
  key: string;
  name: string;
  description: string;
  status: string;
  created_by: string | null;
  created_at: string;
  version_count: number;
  latest_version_id: string | null;
}

export interface RegressionSetVersion {
  id: string;
  regression_set_id: string;
  parent_version_id: string | null;
  version_number: number;
  version_label: string;
  manifest_sha256: string;
  item_count: number;
  status: string;
  created_by: string | null;
  created_at: string;
  sealed_at: string;
}

export interface AnalysisRun {
  run_id: string;
  provider: string;
  created_at?: string;
  dataset_id: string;
  status: string;
  planned: number;
  recorded: number;
  success: number;
  failed: number;
  evaluated: number;
  machine_screened: number;
  audio_quality_statuses: Record<string, number>;
  finding_count: number;
  finding_categories: Record<string, number>;
  asr_evaluated: number;
  asr_failed: number;
  content_evaluated: number;
  cer_mean: number | null;
  wer_mean: number | null;
  sigmos_evaluated: number;
  speaker_similarity_evaluated: number;
  speaker_similarity_mean: number | null;
  word_error_rate_mean: number | null;
}

export type MachineScreeningStatus = "pass" | "suspect" | "fail" | "not_evaluated";
export type AudioQualityDimension =
  | "pop_click"
  | "discontinuity"
  | "noise"
  | "reverb_echo"
  | "clipping_distortion"
  | "codec_artifacts";

export interface ContentAccuracySummary {
  available: boolean;
  enabled: boolean;
  evaluated: number;
  failed: number;
  coverage_percent: number;
  cer_mean: number | null;
  wer_mean: number | null;
  language_counts: Record<string, number>;
}

export interface SigmosDimensionSummary {
  mean: number | null;
  count: number;
}

export interface AnalysisResponse {
  run_total?: number;
  run_offset?: number;
  run_limit?: number;
  top_runs?: AnalysisRun[];
  generated_at: string;
  summary: {
    planned: number;
    recorded: number;
    success: number;
    failed: number;
    evaluated: number;
    machine_screened: number;
    content_evaluated: number;
    sigmos_evaluated: number;
    findings: number;
    quality_coverage_percent: number;
    machine_screening_coverage_percent: number;
    asr_enabled: boolean;
    asr_evaluated: number;
    asr_failed: number;
    asr_coverage_percent: number;
    word_error_rate_mean: number | null;
    content_accuracy: ContentAccuracySummary;
    sigmos_p804: {
      configured: boolean;
      evaluated: number;
      coverage_percent: number;
      dimensions: Record<string, SigmosDimensionSummary>;
    };
    speaker_similarity: {
      configured: boolean;
      evaluated: number;
      coverage_percent: number;
      cosine_similarity_mean: number | null;
      assessment: "uncalibrated";
    };
  };
  groups: {
    providers: Record<string, number>;
    voices: Record<string, number>;
    finding_categories: Record<string, number>;
    finding_labels: Record<string, number>;
    quality_statuses: Record<string, number>;
    dimension_statuses: Record<string, Record<string, number>>;
    audio_quality_statuses: Record<string, number>;
    audio_quality_dimensions: Record<AudioQualityDimension, Record<MachineScreeningStatus, number>>;
    prosody_boundaries: Record<string, number>;
  };
  runs: AnalysisRun[];
  samples: Array<Record<string, unknown>>;
}

export interface PlanRequest {
  luna?: {model_id:"luna-tts"|"luna-tts-1.5"|"luna-tts-character";environment?:"production"|"test";volume:number;scene:"normal"|"whisper";expressivity:"normal"|"strong";text_normalization:boolean};
  run_name: string;
  dataset_id: string;
  run_id: string;
  provider: Provider;
  voice_ids: string[];
  repeats: number;
  concurrency: number;
  assignment_mode: "cartesian" | "round_robin" | "paired";
  speed: number;
  audio_format: "wav" | "mp3" | "pcm";
  quality_detection: boolean;
  mos_enabled: boolean;
  asr_enabled: boolean;
  selected_optional_metric_keys?: string[];
  evaluation_profile_version_id?: string;
  quality_gate_policy_version_id?: string;
  priority: "low" | "medium" | "high";
  voice_catalog_version: string;
  voice_catalog_fetched_at: string;
  voice_catalog_stale: boolean;
}

export interface MetricDefinitionVersion {
  id: string;
  metric_key: string;
  version: string;
  display_name: string;
  description: string;
  category: string;
  value_type: "number" | "integer" | "boolean" | "string" | "object";
  unit: string;
  direction: "higher_better" | "lower_better" | "neutral";
  minimum_value: number | null;
  maximum_value: number | null;
  evaluator_key: string;
  implementation_revision: string;
  output_schema: Record<string, unknown>;
  aggregation: Record<string, unknown>;
  presentation: Record<string, unknown>;
  status: "active" | "deprecated" | "disabled";
  content_sha256: string;
  evaluator_available: boolean;
  current_deployment?: boolean;
  unavailable_reason: string | null;
  created_at: string;
}

export interface EvaluationProfileVersion {
  languages?: string[];
  quality_gate_policy_version_id?: string | null;
  quality_gate_name?: string | null;
  quality_gate_available?: boolean;
  contract_source?: 'explicit'|'legacy_system_default';
  id: string;
  profile_key: string;
  version: string;
  display_name: string;
  description: string;
  metrics: Array<{
    metric_key: string;
    metric_version: string;
    metric_version_id: string | null;
    display_name: string;
    evaluator_key: string | null;
    required: boolean;
    available: boolean;
    config: Record<string, unknown>;
  }>;
  status: "active" | "deprecated" | "disabled";
  content_sha256: string;
  runnable: boolean;
  current_deployment: boolean;
  missing_required_metrics: string[];
  unavailable_optional_metrics: string[];
  created_by: string | null;
  created_at: string;
}

export interface QualityGatePolicyVersion {
  system_default?: boolean;
  current_deployment?: boolean;
  id: string;
  policy_key: string;
  version: string;
  display_name: string;
  description: string;
  rules: Array<{
    rule_id: string;
    metric_key: string;
    metric_version: string;
    operator: "lt" | "lte" | "gt" | "gte" | "eq" | "neq";
    threshold: number | string | boolean;
    severity: "warn" | "block";
    missing: "ignore" | "incomplete" | "warn" | "block";
    description: string;
  }>;
  status: "active" | "deprecated" | "disabled";
  content_sha256: string;
  created_by: string | null;
  created_at: string;
}

export interface RunMetricResponse {
  run_id: string;
  total: number;
  returned: number;
  offset: number;
  limit: number;
  summaries: Array<{
    metric_version_id: string;
    metric_key: string;
    metric_version: string;
    statuses: Record<string, number>;
    count: number;
    mean: number | null;
    minimum: number | null;
    maximum: number | null;
  }>;
  items: Array<{
    id: string;
    run_task_id: string;
    attempt_id: string;
    metric_version_id: string;
    metric_key: string;
    metric_version: string;
    display_name: string;
    category: string;
    unit: string;
    direction: string;
    status: string;
    scalar_value: number | null;
    value: Record<string, unknown>;
    evidence: Record<string, unknown> | null;
    provenance: Record<string, unknown>;
    error_code: string | null;
    created_at: string;
  }>;
}

export interface RunQualityGateResponse {
  run_id: string;
  total: number;
  statuses: Record<string, number>;
  items: Array<{
    id: string;
    run_task_id: string;
    attempt_id: string;
    policy_version_id: string;
    policy_key: string;
    policy_version: string;
    status: "pass" | "warn" | "block" | "incomplete";
    rule_results: Array<Record<string, unknown>>;
    summary: Record<string, unknown>;
    created_at: string;
  }>;
}

export interface RunComparison {
  id: string;
  baseline_run_id: string;
  candidate_run_id: string;
  profile_sha256: string;
  specification: Record<string, unknown>;
  compatibility: {
    compatible: boolean;
    dataset_version_match: boolean;
    profile_match: boolean;
    task_matrix_match: boolean;
    task_count: number;
  };
  result: {
    metric_count: number;
    metrics: Array<{
      metric_key: string;
      metric_version: string;
      metric_version_id: string;
      display_name: string;
      value_type: string;
      unit: string;
      direction: string;
      task_count: number;
      paired_count: number;
      numeric_pair_count: number;
      coverage_percent: number;
      baseline_mean: number | null;
      candidate_mean: number | null;
      raw_delta: number | null;
      improvement: number | null;
      baseline_statuses: Record<string, number>;
      candidate_statuses: Record<string, number>;
    }>;
  };
  content_sha256: string;
  created_by: string | null;
  created_at: string;
}

export interface AsrCapabilities {
  enabled: boolean;
  service_reachable: boolean | null;
  health_status?: string;
  provider: string;
  model: string;
  model_revision: string;
  supported_audio_formats: string[];
  supported_languages: string[];
  reason: string | null;
}

export interface ReferenceBoundaryCapabilities {
  enabled: boolean;
  configured: boolean;
  executable: boolean;
  sigmos_available: boolean;
  asr_available?: boolean;
  reason: string | null;
  protocol_version: string;
  limits: {
    max_targets: number;
    max_voices: number;
    max_references: number;
    max_text_cases: number;
    max_seeds: number;
    max_speeds: number;
    max_cases_per_batch?: number;
    max_cases: number;
    max_direct_cases?: number;
    max_direct_text_cases?: number;
  };
  speed: {
    min: number;
    max: number;
    step: number;
  };
}

export interface ReferenceBoundaryCheckpoint {
  checkpoint_id: string;
  display_name: string;
  status: string;
}

export interface ReferenceBoundaryModel {
  experiment_id: string;
  display_name: string;
  checkpoints: ReferenceBoundaryCheckpoint[];
}

export interface ReferenceBoundaryVoice {
  physical_voice_id?: string;
  voice_id?: string;
  display_name?: string | null;
  language: string | null;
  status: string | null;
}

export interface ReferenceBoundaryReference {
  reference_id: string;
  reference_source?: "catalog" | "library" | "uploaded";
  display_name?: string | null;
  physical_voice_id?: string | null;
  language: string | null;
  voice_ids?: string[];
  status: string | null;
  tags?: string[];
}

export interface ReferenceBoundaryCatalog {
  enabled: boolean;
  available: boolean;
  status: "available" | "partial" | "disabled" | "unavailable" | "error";
  schema_version: number | null;
  snapshot_id: string | null;
  fetched_at: string | null;
  stale: boolean;
  warning: string | null;
  deployment_id: string | null;
  manifest_sha256: string | null;
  admission_status: "pending" | "approved" | null;
  admission_source: "observed" | "bootstrap_config" | "admin" | null;
  admitted_by: string | null;
  admitted_at: string | null;
  registry_matches_configuration: boolean | null;
  models: ReferenceBoundaryModel[];
  voices: ReferenceBoundaryVoice[];
  references: ReferenceBoundaryReference[];
}

export type ReferenceBoundaryTextSelection = {
  case_ids?: string[];
  type: "dataset";
  limit?: number;
};

export interface ReferenceBoundaryTarget {
  experiment_id: string;
  checkpoint_id: string;
}

export interface ReferenceBoundaryDryRunRequest {
  uploaded_references?: Array<{reference_upload_id:string;physical_voice_id:string;primary_style_level:"L1"|"L2"|"L3"|"L4"}>;
  execution_transport?: "remote_test_run" | "direct_v63_tts";
  run_id: string;
  name: string;
  dataset_id: string;
  catalog_snapshot_id: string;
  targets: ReferenceBoundaryTarget[];
  reference_voice_ids: string[];
  reference_selector: {
    type: "explicit_ids";
    reference_ids: string[];
  };
  text_selection: ReferenceBoundaryTextSelection;
  seeds: number[];
  speeds: number[];
  quality_detection: boolean;
  mos_enabled: boolean;
  asr_enabled?: boolean;
}

export interface ReferenceBoundaryDryRunResponse {
  cases?: Array<{reference_style_level?: string; [key:string]: unknown}>;
  execution_transport?: "remote_test_run" | "direct_v63_tts";
  run_id: string;
  status: "planned";
  catalog_snapshot_id: string;
  deployment_id: string;
  manifest_sha256: string;
  admission_status: "pending" | "approved";
  selection_id: string;
  plan_id: string;
  plan_sha256: string;
  case_count: number;
  batch_size: number;
  batch_count: number;
  batches: Array<{
    batch_index: number;
    batch_id: string;
    batch_sha256: string;
    case_count: number;
    reference_count: number;
    text_count: number;
  }>;
  summary: {
    targets: number;
    voices: number;
    references: number;
    texts: number;
    seeds: number;
    speeds: number;
    total: number;
  };
  warnings: string[];
  billable: false;
}

export interface ReferenceBoundaryAdmission {
  deployment_id: string;
  manifest_sha256: string;
  status: "pending" | "approved";
  approval_source: "observed" | "bootstrap_config" | "admin";
  approved_by: string | null;
  approved_at: string | null;
  first_seen_at: string;
  last_seen_at: string;
}

export const valueOf = (record: Record<string, unknown>, key: string, fallback = "—") => {
  const value = record[key];
  return typeof value === "string" || typeof value === "number" ? String(value) : fallback;
};

export function estimateTaskCount(
  caseCount: number,
  voiceCount: number,
  repeats: number,
  mode: PlanRequest["assignment_mode"],
) {
  return mode === "cartesian" ? caseCount * voiceCount * repeats : caseCount * repeats;
}

export function physicalVoiceIdOf(voice: ReferenceBoundaryVoice) {
  return voice.physical_voice_id || voice.voice_id || "";
}

export function isReferenceCompatibleWithVoice(
  reference: ReferenceBoundaryReference,
  physicalVoiceId: string,
) {
  if (!physicalVoiceId || ["disabled", "hidden", "unavailable"].includes(reference.status ?? "")) return false;
  const linkedVoiceIds = reference.physical_voice_id
    ? [reference.physical_voice_id]
    : reference.voice_ids ?? [];
  return linkedVoiceIds.includes(physicalVoiceId);
}

export function referenceDisplayName(reference: ReferenceBoundaryReference) {
  return reference.display_name?.trim() || reference.reference_id;
}

export function referenceRatings(reference: ReferenceBoundaryReference) {
  return [...new Set((reference.tags ?? [])
    .map((tag) => tag.trim().toUpperCase())
    .filter((tag) => /^L[1-4]$/.test(tag)))];
}

export function referenceRating(reference: ReferenceBoundaryReference) {
  return referenceRatings(reference).join("/") || null;
}

export function referenceBoundaryCatalogGate(catalog: ReferenceBoundaryCatalog) {
  if (!catalog.enabled || catalog.status === "disabled") {
    return { ready: false, reason: catalog.warning || "V63 Catalog 功能未启用" };
  }
  if (!catalog.available || ["unavailable", "error"].includes(catalog.status)) {
    return { ready: false, reason: catalog.warning || "V63 Catalog 当前不可用" };
  }
  if (catalog.status === "partial") {
    return { ready: false, reason: catalog.warning || "V63 Catalog 仅返回部分目录，禁止生成 Dry Run" };
  }
  if (!catalog.snapshot_id || !catalog.deployment_id || !catalog.manifest_sha256) {
    return { ready: false, reason: "Catalog 缺少不可变 Snapshot 或 Registry 事实，禁止生成 Dry Run" };
  }
  return { ready: true, reason: null };
}

export function shouldResetReferenceBoundarySelection(
  previousSnapshotId: string | null | undefined,
  nextSnapshotId: string | null,
) {
  return previousSnapshotId !== undefined && previousSnapshotId !== nextSnapshotId;
}

export function isReferenceBoundaryCheckpointSelectable(
  models: ReferenceBoundaryModel[],
  experimentId: string,
  checkpointId: string,
) {
  const checkpoint = models
    .find((model) => model.experiment_id === experimentId)
    ?.checkpoints.find((item) => item.checkpoint_id === checkpointId);
  return checkpoint?.status === "enabled";
}

export function encodeReferenceBoundaryTarget(target: ReferenceBoundaryTarget) {
  return JSON.stringify([target.experiment_id, target.checkpoint_id]);
}

export function decodeReferenceBoundaryTarget(value: string): ReferenceBoundaryTarget | null {
  try {
    const parsed: unknown = JSON.parse(value);
    if (
      !Array.isArray(parsed) ||
      parsed.length !== 2 ||
      typeof parsed[0] !== "string" ||
      typeof parsed[1] !== "string" ||
      !parsed[0] ||
      !parsed[1]
    ) return null;
    return { experiment_id: parsed[0], checkpoint_id: parsed[1] };
  } catch {
    return null;
  }
}

export function selectReferenceIdsWithinLimit(
  references: ReferenceBoundaryReference[],
  physicalVoiceIds: string[],
  limit: number,
) {
  if (limit < 1) return [];
  const selected: string[] = [];
  const selectedIds = new Set<string>();

  // Cover every chosen voice first, then fill the remaining capacity in catalog order.
  for (const voiceId of physicalVoiceIds) {
    const alreadyCovered = references.some((reference) =>
      selectedIds.has(reference.reference_id) && isReferenceCompatibleWithVoice(reference, voiceId));
    if (alreadyCovered) continue;
    const reference = references.find((candidate) =>
      !selectedIds.has(candidate.reference_id) && isReferenceCompatibleWithVoice(candidate, voiceId));
    if (!reference || selected.length >= limit) continue;
    selected.push(reference.reference_id);
    selectedIds.add(reference.reference_id);
  }

  for (const reference of references) {
    if (selected.length >= limit) break;
    if (selectedIds.has(reference.reference_id)) continue;
    selected.push(reference.reference_id);
    selectedIds.add(reference.reference_id);
  }
  return selected;
}

export function estimateReferenceBoundaryTaskCount(input: {
  targetCount: number;
  referenceCount: number;
  textCount: number;
  seedCount: number;
  speedCount: number;
}) {
  return input.targetCount * input.referenceCount * input.textCount * input.seedCount * input.speedCount;
}

export function estimateReferenceBoundaryBatchCount(input: {
  targetCount: number;
  referenceCount: number;
  textCount: number;
  seedCount: number;
  speedCount: number;
}, batchSize: number) {
  const casesPerReferenceText = input.targetCount * input.seedCount * input.speedCount;
  if (
    batchSize < 1 ||
    casesPerReferenceText < 1 ||
    casesPerReferenceText > batchSize ||
    input.referenceCount < 1 ||
    input.textCount < 1
  ) return 0;

  const textsPerBatch = Math.max(1, Math.floor(batchSize / casesPerReferenceText));
  let remainingTexts = input.textCount;
  let batchCount = 0;
  while (remainingTexts > 0) {
    const textCount = Math.min(textsPerBatch, remainingTexts);
    const referencesPerBatch = Math.max(
      1,
      Math.floor(batchSize / (textCount * casesPerReferenceText)),
    );
    batchCount += Math.ceil(input.referenceCount / referencesPerBatch);
    remainingTexts -= textCount;
  }
  return batchCount;
}
